import { useState } from "react";

import { useTranslation } from "react-i18next";

import { CheckIcon, ChevronsUpDownIcon, XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/utils/helpers";

import type { EntityDescriptor } from "./EntityDescriptor";
import { useEntitySelection } from "./useEntitySelection";

export interface EntityComboboxProps<T> {
  value: number | null;
  onChange: (id: number | null) => void;
  /** The entity wiring (data hooks + id/label + i18n). Single source of truth. */
  descriptor: EntityDescriptor<T>;
  /** Debounce delay in ms applied to the search query before calling useEntities. Defaults to 200ms. */
  debounceMs?: number;
  /** Whether to show the "— (not planned)" / "(none)" clearing option at the bottom. */
  allowClear?: boolean;
  /** Placeholder text on the search input. */
  placeholder?: string;
  /** Text shown when value === null and allowClear is true. */
  emptyLabel?: string;
  size?: "sm" | "default";
  variant?: "outline" | "ghost";
  className?: string;
  /** Override trigger styling (e.g. matrix cell uses ghost+sm with extra constraints). */
  triggerClassName?: string;
  /** Optional: custom rendering of the trigger content. Defaults to `renderLabel(selected)` or emptyLabel. */
  renderTriggerContent?: (selected: T | null) => React.ReactNode;
  /** Disable the trigger (e.g. during a pending mutation). */
  disabled?: boolean;
  /** Aria label override (defaults to selected entity's label / emptyLabel). */
  ariaLabel?: string;
  /** If true, hide the chevron icon (useful for compact cell contexts). */
  hideChevron?: boolean;
}

export function EntityCombobox<T>({
  descriptor,
  value,
  onChange,
  debounceMs = 200,
  allowClear = false,
  placeholder,
  emptyLabel,
  size = "default",
  variant = "outline",
  className,
  triggerClassName,
  renderTriggerContent,
  disabled = false,
  ariaLabel,
  hideChevron = false,
}: EntityComboboxProps<T>) {
  const { useEntities, useResolveSelected, getEntityId, renderLabel } =
    descriptor;
  const { t } = useTranslation();
  const resolvedPlaceholder =
    placeholder ??
    t(
      descriptor.i18n?.searchPlaceholder ??
        "components.entitySelect.searchPlaceholder",
    );
  const resolvedEmptyLabel =
    emptyLabel ??
    t(descriptor.i18n?.emptyLabel ?? "components.entitySelect.emptyLabel");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, debounceMs);

  const { selectedEntities, results, isLoading } = useEntitySelection<T>({
    value: value != null ? [value] : [],
    query: debouncedQuery,
    useSearchResults: useEntities,
    useResolveSelected,
    getId: getEntityId,
  });

  const selected = selectedEntities[0] ?? null;
  const triggerLabel = selected ? renderLabel(selected) : resolvedEmptyLabel;

  function handleSelect(id: number) {
    onChange(id);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel ?? triggerLabel}
          disabled={disabled}
          className={cn(
            "w-full min-w-0 justify-between gap-2",
            className,
            triggerClassName,
          )}
        >
          {renderTriggerContent ? (
            renderTriggerContent(selected)
          ) : (
            <span
              className={cn("truncate", !selected && "text-muted-foreground")}
            >
              {triggerLabel}
            </span>
          )}
          {allowClear &&
          value != null &&
          !hideChevron &&
          !renderTriggerContent ? (
            <span
              role="button"
              // tabIndex={-1} is deliberate: keep the trigger a single tab stop.
              // Keyboard users clear via the "(none)" row inside the popover.
              tabIndex={-1}
              aria-label={t("components.entitySelect.clear")}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              className="ml-auto shrink-0 rounded-sm opacity-50 hover:opacity-100"
            >
              <XIcon className="size-4" aria-hidden />
            </span>
          ) : (
            !hideChevron && (
              <ChevronsUpDownIcon
                className="size-4 shrink-0 opacity-50"
                aria-hidden
              />
            )
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={resolvedPlaceholder}
            className="h-9"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {isLoading && (
              <div className="text-muted-foreground p-2 text-sm">
                {t("common.loading")}
              </div>
            )}
            <CommandEmpty>{t("common.noResults")}</CommandEmpty>
            {selectedEntities.length > 0 && (
              <>
                <CommandGroup
                  heading={t("components.entitySelect.selectedHeading")}
                >
                  {selectedEntities.map((entity) => {
                    const id = getEntityId(entity);
                    const label = renderLabel(entity);
                    return (
                      <CommandItem
                        key={id}
                        value={`selected-${label}-${id}`}
                        onSelect={() => handleSelect(id)}
                      >
                        {label}
                        <CheckIcon className="ml-auto size-4 opacity-100" />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>
              {results.map((entity) => {
                const id = getEntityId(entity);
                const label = renderLabel(entity);
                return (
                  <CommandItem
                    key={id}
                    value={`${label}-${id}`}
                    onSelect={() => handleSelect(id)}
                  >
                    {label}
                    <CheckIcon
                      className={cn(
                        "ml-auto size-4",
                        value === id ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {allowClear && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onChange(null);
                      setOpen(false);
                    }}
                  >
                    <span className="text-muted-foreground">
                      {resolvedEmptyLabel}
                    </span>
                    <CheckIcon
                      className={cn(
                        "ml-auto size-4",
                        value === null ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
